import assert from 'assert';
import {AxiosError} from 'axios';
import {GetStaticPaths, GetStaticProps, GetStaticPropsContext} from 'next';

import {
    BrowseAggregatorContent,
    BrowsePageContent,
    BrowsePageContentUtil,
    CertificationAggregatorApi,
    CertificationBrowseAggregatorContentUtil,
    fetchOpenBadgedCertificationTopics,
    fetchRelatedCertificationAggregators,
} from '@udemy/browse-aggregator';
import {RENDER_MODES} from '@udemy/nextjs-core';
import {fetchCourseSearch} from '@udemy/react-discovery-units';
import {udSentry} from '@udemy/sentry';

import {getLayout} from 'src/components/layout';
import {
    CertificationAggregatorPageFeature,
    CertificationAggregatorPageFeatureProps,
} from 'src/features/certification-aggregator';
import {CARD_CAROUSEL_PAGE_SIZE} from 'src/features/certification-aggregator/certification-aggregator';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {FOUR_HOURS} from 'src/lib/constants';
import {fetcher} from 'src/lib/gql-fetcher';
import {udemyApi} from 'src/lib/ud-global-fetch';
import {AppContextData} from 'src/types/page-props';

import {getCourseSearchQueryVariables} from '../../../components/aggregator-course-directory/providers/aggregator-course-directory-context-provider.react-component';
import {getStaticAppContext} from '../../api/context.api';

export const getAggregatorContext = async (aggregatorSlug: string, locale: string) => {
    try {
        return await new CertificationAggregatorApi(udemyApi).getCertificationAggregatorContext(
            aggregatorSlug,
            {
                locale,
            },
        );
    } catch (e) {
        if ((e as AxiosError).isAxiosError) {
            const axiosError = e as AxiosError;
            if (axiosError.response?.status === 404) {
                return {
                    notFound: true,
                };
            }
            const axiosErrorDetails = {
                ...axiosError.toJSON(),
            };
            // eslint-disable-next-line no-console
            console.error('Failed to fetch aggregator context due to axios error', {
                error_details: axiosErrorDetails,
                slug: aggregatorSlug,
                locale,
            });
        } else {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch aggregator context', {
                error_details: {
                    message: (e as Error).message,
                    stack: (e as Error).stack,
                },
                slug: aggregatorSlug,
                locale,
            });
        }
        udSentry.captureException(e);
        throw e;
    }
};

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
    const locale = context.locale as string;
    const params = context.params;

    // Fetch certification aggregator content, actual slug is /browse/certification/<slug>. We're using the partial slug here.
    const browseAggregatorPagePartialSlug = params?.aggregator as string;
    const [translations, staticData, aggregatorContextResponse] = await Promise.all([
        getCompactTranslations(locale),
        getStaticAppContext({locale}),
        getAggregatorContext(browseAggregatorPagePartialSlug, locale),
    ]);

    if ((aggregatorContextResponse as {notFound: boolean}).notFound) {
        return {
            notFound: true,
        };
    }

    const api = new CertificationAggregatorApi(udemyApi, fetcher);
    const aggregatorContent = new BrowsePageContentUtil(
        aggregatorContextResponse as BrowsePageContent,
    ).getBrowseAggregatorContent();

    const courseSearchVariables = getCourseSearchQueryVariables(aggregatorContent);

    let relatedAggregatorsComponentInitialData = null;
    let certificationTopicCarouselComponentInitialData;
    let courseSearchInitialData;

    try {
        const [relatedAggregatorsResult, certificationTopicCarouselResult, courseSearchResult] =
            await Promise.allSettled([
                fetchRelatedCertificationAggregators(
                    api,
                    aggregatorContent as BrowseAggregatorContent,
                    1,
                    CARD_CAROUSEL_PAGE_SIZE,
                    locale,
                ),
                fetchOpenBadgedCertificationTopics(
                    api,
                    'by_descriptor_id',
                    new CertificationBrowseAggregatorContentUtil(
                        aggregatorContent as BrowseAggregatorContent,
                    )?.getAssignedTopicDescriptor()?.entity_value ?? '',
                    1,
                    CARD_CAROUSEL_PAGE_SIZE,
                ),
                fetchCourseSearch(fetcher, courseSearchVariables),
            ]);

        // Handle relatedAggregatorsResult
        if (relatedAggregatorsResult.status === 'fulfilled') {
            relatedAggregatorsComponentInitialData = relatedAggregatorsResult.value;
        } else if (relatedAggregatorsResult.reason?.response?.status === 404) {
            // eslint-disable-next-line no-console
            console.warn(
                `Related certification aggregators not found (404) for ${aggregatorContent?.aggregator_name}.`,
                {aggregatorContent, slug: browseAggregatorPagePartialSlug, locale},
            );
        } else {
            throw relatedAggregatorsResult.reason;
        }

        if (certificationTopicCarouselResult.status == 'fulfilled') {
            certificationTopicCarouselComponentInitialData = certificationTopicCarouselResult.value;
        } else {
            throw certificationTopicCarouselResult.reason;
        }

        if (courseSearchResult.status === 'fulfilled') {
            courseSearchInitialData = courseSearchResult.value;
        }
    } catch (error) {
        if ((error as AxiosError).isAxiosError) {
            const axiosError = error as AxiosError;
            const axiosErrorDetails = {...axiosError.toJSON()};
            // eslint-disable-next-line no-console
            console.error('Failed to fetch aggregators component data due to axios error', {
                error_details: axiosErrorDetails,
                aggregatorContent,
                slug: browseAggregatorPagePartialSlug,
                locale,
            });
        } else {
            // eslint-disable-next-line no-console
            console.error('Unexpected error occurred while fetching aggregators component data', {
                /**
                 * Don't know why but when we use error object directly, winston is just ignoring it. So, we're using
                 * error.message, error.stack, and error.cause (in case of react-query errors).
                 * Also, we're gonna be logging it anyway after this so I think these are
                 * more than enough to log.
                 */
                error_details: {
                    message: (error as Error).message,
                    stack: (error as Error).stack,
                    cause: (error as Error).cause,
                },
                aggregatorContent,
                courseSearchVariables,
                slug: browseAggregatorPagePartialSlug,
                locale,
            });
        }
        udSentry.captureException(error);
        throw error;
    }

    assert(
        certificationTopicCarouselComponentInitialData?.content?.length > 0,
        'certificationTopicCarouselComponentInitialData should not be empty',
    );

    assert(
        courseSearchInitialData?.courseSearch?.results?.length ?? 0 > 0,
        'courseSearchInitialData should not be empty',
    );

    const appContext: AppContextData = {...staticData};

    const pageKey = new CertificationBrowseAggregatorContentUtil(
        aggregatorContent as BrowseAggregatorContent,
    ).isIssuerAggregator()
        ? 'browse_certification_issuer_aggregator'
        : 'browse_certification_subject_aggregator';

    return {
        props: {
            appContext,
            locale,
            pageKey,
            renderMode: RENDER_MODES.ISR,
            translations,
            params,
            browsePageContent: aggregatorContextResponse,
            relatedAggregatorsComponentInitialData,
            certificationTopicCarouselComponentInitialData,
            courseSearchInitialData,
        },
        revalidate: FOUR_HOURS,
    };
};

function CertificationAggregatorPage(props: CertificationAggregatorPageFeatureProps) {
    return <CertificationAggregatorPageFeature {...props} />;
}

CertificationAggregatorPage.getLayout = getLayout;

export default CertificationAggregatorPage;
